import { Grid, GridItem } from '@imported/constellation';
import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { IdvSkippedReason, } from '@cbbt/itf-unverified-individual-customer-domain';
import { PageHeading, ProofOfIndentityForm } from '../../components/index.js';
import NeedHelp from '../../components/NeedHelp/index.js';
import { proofOfIdentitydefaultValues, } from '../../components/ProofOfIndentityForm/types.js';
import { useIdvProvider } from '../../context/index.js';
import { useLabelContext } from '../../context/LabelContext.js';
import { deleteDuCall, getExternalToken, retrieveCaseDetails, } from '../../helper/apiHelper.js';
import { getErrorMessage } from '../../helper/getErrorMessage.js';
import { getOldDocUploadDetails } from '../../helper/referenceHelper.js';
import { DocType, IdvStatus } from '../../types/identityContextTypes.js';
import { INVALID_CASE_REFERENCE_NUMBER, NONE_OPTION_SELECTED, UK_DRIVING_LICENCE, } from '../../utils/constants.js';
import { IdvDeleteDocFailure, IdvGetCaseDetailsError, IdvTokenFailureProofOfIdentity, } from '../../utils/ErrorClasses/index.js';
import { ROUTE_PATHS } from '../../utils/routePaths.js';
const ProofOfIdentity = () => {
    const methods = useForm({
        mode: 'onBlur',
        reValidateMode: 'onChange',
        defaultValues: proofOfIdentitydefaultValues,
    });
    const { handleSubmit } = methods;
    const { idvState, setIdvState } = useIdvProvider();
    const navigate = useNavigate();
    const { applicationProps, docUploadLabClient, svcIdvClient, getJwt, idvStateHandler, } = idvState;
    const { getLabelFromContext } = useLabelContext();
    const deleteOldDoc = useCallback(async (createJwt, individualData) => {
        let caseDetails;
        try {
            const resCaseDetails = await retrieveCaseDetails(individualData, createJwt, docUploadLabClient);
            caseDetails = resCaseDetails.data;
        }
        catch (error) {
            if (error.response?.data.message === INVALID_CASE_REFERENCE_NUMBER) {
                return;
            }
            await idvStateHandler({
                status: IdvStatus.ERROR,
                body: {
                    state: applicationProps,
                    error: new IdvGetCaseDetailsError(`Error at get case details ${getErrorMessage(error)}`),
                    errorPath: 'proofOfIdentity',
                },
            });
            return;
        }
        const deleteObj = {
            caseId: caseDetails.caseId,
            processCode: caseDetails.processCode,
            caseReferenceNo: caseDetails.caseReferenceNo,
            casePartyID: caseDetails?.partyDetails[0]?.casePartyID,
        };
        const getOldDocDetails = getOldDocUploadDetails(caseDetails);
        if (getOldDocDetails.length === 0) {
            return;
        }
        try {
            await deleteDuCall(getOldDocDetails, deleteObj, createJwt, docUploadLabClient);
            setTimeout(() => {
                deleteOldDoc(createJwt, individualData);
            }, 500);
        }
        catch ({ error }) {
            if (error.status === 404) {
                return;
            }
            await idvStateHandler({
                status: IdvStatus.ERROR,
                body: {
                    state: applicationProps,
                    error: new IdvDeleteDocFailure(`Error while deleting docs ${getErrorMessage(error)}`),
                    errorPath: 'proofOfIdentity',
                },
            });
        }
    }, [docUploadLabClient, idvStateHandler, applicationProps]);
    const deleteDocumentWrapper = useCallback(async () => {
        try {
            const tokenResponse = await getJwt();
            const externalToken = await getExternalToken(tokenResponse.jwt, applicationProps.brand, svcIdvClient);
            const createJwtResponse = {
                jwt: externalToken.jwt,
            };
            const individualDataResponse = {
                ...applicationProps.individualData,
                caseRefNumber: tokenResponse.caseRefNumber,
                guid: applicationProps.individualData.dossierId,
                identityVerificationSkipped: false,
            };
            await deleteOldDoc(createJwtResponse, individualDataResponse);
        }
        catch (error) {
            await idvStateHandler({
                status: IdvStatus.ERROR,
                body: {
                    state: applicationProps,
                    error: new IdvTokenFailureProofOfIdentity(`Error at token exchange ${getErrorMessage(error)}`),
                    errorPath: 'proofOfIdentity',
                },
            });
        }
    }, [applicationProps, getJwt, svcIdvClient, deleteOldDoc, idvStateHandler]);
    const handleProofOfIdentitySubmission = useCallback(() => {
        handleSubmit(async (data) => {
            if (applicationProps.individualData.retryCount > 0 &&
                applicationProps.individualData.retryCount < applicationProps.maxRetries) {
                deleteDocumentWrapper();
            }
            const isNoneOptionSeleted = data.documentOption === NONE_OPTION_SELECTED;
            if (isNoneOptionSeleted) {
                setIdvState((prevState) => ({
                    ...prevState,
                    identityState: {
                        country: data.country,
                        bioMetricCheck: data.bioMetricStatus,
                        documentOption: data.documentOption,
                    },
                }));
            }
            else {
                setIdvState((prevState) => ({
                    ...prevState,
                    identityState: {
                        country: data.country,
                        bioMetricCheck: data.bioMetricStatus,
                        documentOption: data.documentOption,
                    },
                    docType: data.documentOption === UK_DRIVING_LICENCE
                        ? DocType.DL_FRONT
                        : DocType.PASSPORT,
                }));
            }
            if (isNoneOptionSeleted) {
                await idvStateHandler({
                    status: IdvStatus.SKIP,
                    body: {
                        state: applicationProps,
                        skipReason: IdvSkippedReason.REQUIRED_ID_DOC_UNAVAILABLE,
                    },
                });
            }
            else if (data.bioMetricStatus === 'Yes') {
                navigate(`../${ROUTE_PATHS.TAKE_PHOTO_CONTENT}`);
            }
            else if (data.bioMetricStatus === 'No') {
                await idvStateHandler({
                    status: IdvStatus.SKIP,
                    body: {
                        state: applicationProps,
                        skipReason: IdvSkippedReason.REQUIRED_ID_DOC_UNAVAILABLE,
                    },
                });
            }
            else if (data.country === 'Country of issue not listed') {
                await idvStateHandler({
                    status: IdvStatus.SKIP,
                    body: {
                        state: applicationProps,
                        skipReason: IdvSkippedReason.PASSPORT_COUNTRY_NOT_APPROVED_FOR_ID_VERIFY,
                    },
                });
            }
            else {
                await idvStateHandler({
                    status: IdvStatus.SKIP,
                    body: {
                        state: applicationProps,
                        skipReason: IdvSkippedReason.REFUSED_BIOMETRIC,
                    },
                });
            }
        })();
    }, [
        handleSubmit,
        deleteDocumentWrapper,
        setIdvState,
        idvStateHandler,
        applicationProps,
        navigate,
    ]);
    const handlePreviousPage = useCallback(async () => await idvStateHandler({
        status: IdvStatus.PREVIOUS,
        body: {
            state: applicationProps,
        },
    }), [idvStateHandler, applicationProps]);
    return (React.createElement(React.Fragment, null,
        React.createElement(PageHeading, { journeyTitle: idvState.labels.applicationHeading, pageTitle: getLabelFromContext('containers.proofOfIdentity.pageTitle') }),
        React.createElement(Grid, null,
            React.createElement(GridItem, { md: 8, sm: 12 },
                React.createElement(ProofOfIndentityForm, { methods: methods, handlePreviousPage: handlePreviousPage, handleProofOfIdentitySubmission: handleProofOfIdentitySubmission })),
            React.createElement(GridItem, { md: 4, sm: 12 },
                React.createElement(NeedHelp, null)))));
};
export default ProofOfIdentity;
//# sourceMappingURL=index.js.map